
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "@/components/ui/dialog";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { useChapters } from "@/hooks/useChapters";
import { useProject } from "@/hooks/useProjects";
import { useStoriesByProject } from "@/hooks/useStories";
import {
    AiPrompt,
    createAiPrompt,
    PromptScope,
    PromptType,
    updateAiPrompt
} from "@/services/supabase/ai-prompts";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Trash2 } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

const formSchema = z.object({
    name: z.string().min(1, "Name is required"),
    content: z.string().min(1, "Content is required"),
    type: z.nativeEnum(PromptType),
    action_label: z.string().optional(),
    is_active: z.boolean().default(true),
    // Scope fields
    scope: z.nativeEnum(PromptScope),
    project_id: z.string().optional(),
    story_id: z.number().optional(),
    chapter_id: z.number().optional(),
});

interface PromptDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    projectId?: string;
    prompt: AiPrompt | null;
    initialType?: PromptType;
    onSuccess: () => void;
    onDelete?: (id: number) => Promise<void> | void;
}

export function PromptDialog({ open, onOpenChange, projectId, prompt, initialType, onSuccess, onDelete }: PromptDialogProps) {
    const { toast } = useToast();
    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            name: "",
            content: "",
            type: initialType || PromptType.SystemInstruction,
            action_label: "",
            is_active: true,
            scope: PromptScope.Global,
            project_id: projectId,
        },
    });

    const scope = form.watch("scope");

    // Fetch context data based on selected scope
    const { data: project } = useProject(projectId, { enabled: !!projectId });
    const { data: stories = [] } = useStoriesByProject(projectId);
    const selectedStoryId = form.watch("story_id");
    const { data: chapters = [] } = useChapters(String(selectedStoryId), { enabled: !!selectedStoryId });

    useEffect(() => {
        if (prompt) {
            form.reset({
                name: prompt.name,
                content: prompt.content,
                type: prompt.type,
                action_label: prompt.action_label || "",
                is_active: prompt.is_active,
                scope: prompt.scope,
                project_id: prompt.project_id || projectId,
                story_id: prompt.story_id || undefined,
                chapter_id: prompt.chapter_id || undefined,
            });
        } else {
            form.reset({
                name: "",
                content: "",
                type: initialType || PromptType.SystemInstruction,
                action_label: "",
                is_active: true,
                scope: PromptScope.Global,
                project_id: projectId,
            });
        }
    }, [prompt, form, projectId, initialType, open]);

    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        try {
            if (prompt) {
                await updateAiPrompt({
                    id: prompt.id,
                    name: values.name,
                    content: values.content,
                    action_label: values.action_label,
                    is_active: values.is_active,
                    // Note: Changing scope/context on edit is complex, usually we don't allow it or handle it carefully.
                    // For now, assuming scope doesn't change on edit for simplicity, or we add that logic.
                    // Let's stick to updating content fields.
                });
                toast({ title: "Success", description: "Prompt updated" });
            } else {
                await createAiPrompt({
                    name: values.name,
                    content: values.content,
                    type: values.type,
                    scope: values.scope,
                    project_id: values.scope === PromptScope.Project ? projectId : undefined,
                    story_id: values.scope === PromptScope.Story ? values.story_id : undefined,
                    chapter_id: values.scope === PromptScope.Chapter ? values.chapter_id : undefined,
                    action_label: values.action_label,
                    is_active: values.is_active,
                });
                toast({ title: "Success", description: "Prompt created" });
            }
            onSuccess();
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to save prompt",
                variant: "destructive",
            });
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[700px] sm:rounded-2xl border-border/60 shadow-2xl p-0 overflow-hidden gap-0">
                <DialogHeader className="p-8 pb-6 border-b border-border/40 bg-muted/5 space-y-1">
                    <DialogTitle className="text-2xl font-semibold text-foreground tracking-tight">{prompt ? "Edit Prompt" : "New Prompt"}</DialogTitle>
                    <DialogDescription className="text-base text-muted-foreground leading-relaxed">
                        {prompt ? "Update your custom prompt details." : "Create a new custom instruction or action for your AI assistant."}
                    </DialogDescription>
                </DialogHeader>

                <div className="max-h-[70vh] overflow-y-auto">
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 px-8 py-8">

                            {/* Top Row: Name and Type */}
                            <div className="grid grid-cols-2 gap-6">
                                <FormField
                                    control={form.control}
                                    name="name"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-foreground/80">Name</FormLabel>
                                            <FormControl>
                                                <Input
                                                    placeholder="e.g. Pirate Persona"
                                                    {...field}
                                                    className="h-11 rounded-xl bg-muted/30 border-muted-foreground/10 focus:bg-background transition-colors"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="type"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-foreground/80">Type</FormLabel>
                                            <Select
                                                onValueChange={(val) => field.onChange(Number(val))}
                                                value={String(field.value)}
                                                disabled={!!prompt}
                                            >
                                                <FormControl>
                                                    <SelectTrigger className="h-11 rounded-xl bg-muted/30 border-muted-foreground/10 focus:bg-background transition-colors">
                                                        <SelectValue placeholder="Select type" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent className="rounded-xl shadow-xl border-border/60">
                                                    <SelectItem value={String(PromptType.SystemInstruction)}>System Instruction</SelectItem>
                                                    <SelectItem value={String(PromptType.WritingAction)}>Writing Action</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            {/* Scope Selection - Only for New Prompts */}
                            {!prompt && (
                                <div className="p-4 rounded-xl border border-border/40 bg-muted/20 space-y-4">
                                    <FormField
                                        control={form.control}
                                        name="scope"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className="text-foreground/80">Scope</FormLabel>
                                                <Select
                                                    onValueChange={(val) => {
                                                        field.onChange(Number(val));
                                                        // Reset child selections
                                                        form.setValue("story_id", undefined);
                                                        form.setValue("chapter_id", undefined);
                                                    }}
                                                    value={String(field.value)}
                                                >
                                                    <FormControl>
                                                        <SelectTrigger className="bg-background">
                                                            <SelectValue placeholder="Select scope" />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        <SelectItem value={String(PromptScope.Global)}>Global (Everywhere)</SelectItem>
                                                        <SelectItem value={String(PromptScope.Project)}>Project ({project?.name || 'Current'})</SelectItem>
                                                        <SelectItem value={String(PromptScope.Story)}>Story</SelectItem>
                                                        <SelectItem value={String(PromptScope.Chapter)}>Chapter</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    {/* Story Selection */}
                                    {(scope === PromptScope.Story || scope === PromptScope.Chapter) && (
                                        <FormField
                                            control={form.control}
                                            name="story_id"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel className="text-foreground/80">Story</FormLabel>
                                                    <Select
                                                        onValueChange={(val) => {
                                                            field.onChange(Number(val));
                                                            form.setValue("chapter_id", undefined);
                                                        }}
                                                        value={field.value ? String(field.value) : undefined}
                                                    >
                                                        <FormControl>
                                                            <SelectTrigger className="bg-background">
                                                                <SelectValue placeholder="Select a story" />
                                                            </SelectTrigger>
                                                        </FormControl>
                                                        <SelectContent>
                                                            {stories.map(s => (
                                                                <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    )}

                                    {/* Chapter Selection */}
                                    {scope === PromptScope.Chapter && (
                                        <FormField
                                            control={form.control}
                                            name="chapter_id"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel className="text-foreground/80">Chapter</FormLabel>
                                                    <Select
                                                        onValueChange={(val) => field.onChange(Number(val))}
                                                        value={field.value ? String(field.value) : undefined}
                                                        disabled={!selectedStoryId}
                                                    >
                                                        <FormControl>
                                                            <SelectTrigger className="bg-background">
                                                                <SelectValue placeholder="Select a chapter" />
                                                            </SelectTrigger>
                                                        </FormControl>
                                                        <SelectContent>
                                                            {chapters.map(c => (
                                                                <SelectItem key={c.id} value={String(c.id)}>
                                                                    {c.title || `Chapter ${(c.display_order || 0) + 1}`}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    )}
                                </div>
                            )}

                            {/* Writing Action Specifics */}
                            {form.watch("type") === PromptType.WritingAction && (
                                <div className="w-full">
                                    <FormField
                                        control={form.control}
                                        name="action_label"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className="text-foreground/80">Button Label</FormLabel>
                                                <FormControl>
                                                    <Input
                                                        placeholder="e.g. Rewrite"
                                                        {...field}
                                                        className="h-11 rounded-xl bg-muted/30 border-muted-foreground/10 focus:bg-background"
                                                    />
                                                </FormControl>
                                                <FormDescription>Short text for the action button</FormDescription>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            )}

                            {/* Content */}
                            <FormField
                                control={form.control}
                                name="content"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-foreground/80">Prompt Content</FormLabel>
                                        <FormControl>
                                            <Textarea
                                                placeholder="You are a helpful assistant..."
                                                className="min-h-[200px] rounded-xl bg-muted/30 border-muted-foreground/10 focus:bg-background text-base resize-y"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Active Status */}
                            <FormField
                                control={form.control}
                                name="is_active"
                                render={({ field }) => (
                                    <FormItem className="flex flex-row items-center justify-between rounded-xl border border-border/40 bg-muted/20 p-4">
                                        <div className="space-y-0.5">
                                            <FormLabel className="text-base">Active Status</FormLabel>
                                            <FormDescription>
                                                Enable or disable this prompt without deleting it.
                                            </FormDescription>
                                        </div>
                                        <FormControl>
                                            <Switch
                                                checked={field.value}
                                                onCheckedChange={field.onChange}
                                                className="data-[state=checked]:bg-green-600 dark:data-[state=checked]:bg-green-500"
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                        </form>
                    </Form>
                </div>

                <DialogFooter className={`p-6 bg-muted/5 border-t border-border/40 ${prompt ? 'flex sm:justify-between' : ''}`}>
                    {prompt && (
                        <Button
                            variant="ghost"
                            onClick={() => {
                                if (onDelete) {
                                    onDelete(prompt.id);
                                    onOpenChange(false);
                                }
                            }}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10 rounded-xl h-11 px-6 mr-auto"
                        >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                        </Button>
                    )}
                    <div className="flex gap-2">
                        <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-xl h-11 px-6">
                            Cancel
                        </Button>
                        <Button
                            onClick={form.handleSubmit(onSubmit)}
                            disabled={form.formState.isSubmitting}
                            className="rounded-xl h-11 px-8 shadow-md"
                        >
                            {form.formState.isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {prompt ? "Save Changes" : "Create Prompt"}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
